<template>
  <div :class="[{ 'flex items-center': isHorizontal }, $attrs.class]" :style="$attrs.style">
    <div class="flex justify-between">
      <label v-if="label" :for="fieldName" class="text-2xs font-bold text-grey-dark">
        <p :class="labelClass">{{ label }}</p>
      </label>
      <slot name="labelIcon"></slot>
    </div>
    <div class="relative w-full">
      <input
        v-bind="inputAttrs"
        class="w-full border-1 border-grey-lighter focus:border-primary outline-none text-sm text-grey-dark"
        :class="inputClass"
        :id="fieldName"
        :name="fieldName"
        :value="inputValue"
        :type="type"
        :disabled="disabled"
        @input="onInput"
        @change="onChange"
        @blur="onBlur"
        @keyup.enter="$emit('submit')"
      />
      <slot></slot>
      <!-- error message -->
      <slot name="error">
        <div v-if="errorMessage && !disabled" class="absolute text-red text-3xs truncate w-full" :title="errorMessage">
          {{ errorMessage }}
        </div>
      </slot>

      <!-- icons -->
      <div class="absolute inset-y-0 right-0 flex items-center mr-3">
        <div v-if="isSearchIcon" @click.stop="$emit('submit')">
          <slot name="search"></slot>
        </div>
        <slot name="icon" v-else></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { useField } from 'vee-validate'
import { computed, watch } from 'vue'

export default {
  /* Handled manually so layout classes stay on the wrapper while everything else
     (placeholder, autocomplete, maxlength, ...) reaches the <input>. With the
     default fallthrough all of it piled onto the wrapper div instead. */
  inheritAttrs: false,

  props: {
    isHorizontal: { type: Boolean, default: false },
    isSearchIcon: { type: Boolean, default: false },
    id: { type: String },
    label: { type: String, default: '' },
    labelClass: { type: String, default: '' },
    inputClass: { type: String, default: '' },
    initialValidate: { type: Boolean, default: false },
    rules: { type: [String, Object], default: '' },
    modelValue: { type: [String, Number], default: '' },
    type: { type: String, default: 'text' },
    disabled: { type: Boolean, default: false }
  },

  emits: ['update:modelValue', 'input', 'change', 'blur', 'submit'],

  setup (props, { emit, attrs }) {
    const fieldName = computed(() => attrs.name || props.id || 'field')

    // class/style belong to the wrapper; the rest is for the input itself
    const inputAttrs = computed(() => {
      const { class: className, style, ...rest } = attrs

      return rest
    })

    /* A disabled field can't be corrected by the user, so it must not gate
       submit. vee-validate has no notion of `disabled`, and the template hides
       errors for disabled fields - together that meant an empty disabled
       required field blocked the form with nothing on screen explaining why.
       Passed as a computed because useField unwraps `rules` inside a computed of
       its own, so this re-evaluates whenever `disabled` changes. */
    const activeRules = computed(() => (props.disabled ? undefined : props.rules))

    const {
      value: inputValue,
      errorMessage,
      handleBlur,
      handleChange,
      setValue,
      validate
    } = useField(fieldName.value, activeRules, {
      initialValue: props.modelValue,
      /* Drives the {field} placeholder in validation messages - without it they
         read "The confirm-new-password field is required". */
      label: props.label || fieldName.value,
      validateOnValueUpdate: false,
      // Covers the case where the flag is already true on mount
      validateOnMount: props.initialValidate
    })

    watch(
      () => props.modelValue,
      newValue => {
        if (newValue !== inputValue.value) {
          setValue(newValue)
        }
      }
    )

    // ...and this covers it being flipped on later, e.g. on a failed submit
    watch(
      () => props.initialValidate,
      shouldValidate => {
        if (shouldValidate) validate()
      }
    )

    const onChange = e => {
      const value = e.target.value
      handleChange(value)
      emit('update:modelValue', value)
      emit('change', value)
    }

    const onInput = e => {
      const value = e.target.value
      /* Sync the field without validating, so validation state stays correct even
         when a parent doesn't bind v-model. */
      handleChange(value, false)
      emit('update:modelValue', value)
      emit('input', value)
    }

    const onBlur = e => {
      handleBlur()
      emit('blur', e)
    }

    return {
      fieldName,
      inputAttrs,
      inputValue,
      errorMessage,
      onChange,
      onInput,
      onBlur
    }
  }
}
</script>
